import React, { lazy } from 'react';
import { Text } from 'react-native';
import { createSelectorCreator, defaultMemoize } from 'reselect';
import axios from 'axios';
import gsap from 'gsap';

import {
  MINUTES_UNTIL_TIMEOUT,
  PROMISE_STATUSES,
  PRODUCT_LECTURA,
  PRODUCT_READING,
  PROGRAMS,
  SELECTED_PRODUCT_KEY,
} from 'lib/constants';
import GameDisplayNames from 'a/hub/data/GameDisplayNames';
import AuthService from 'root/lib/authService';

import GameList from '../activities/hub/data/GamesList';
import BookList from '../activities/hub/data/BookList';
import Env from './Env';
import { FORMATTED_GRADES, GRADES, SEASON } from '../activities/hub/data/constants';
import gameIds from '../activities/hub/data/GameIds';
import STEP_TYPE from '../activities/hub/data/StepTypes';
import redirectToLogin from '../activities/api/redirectToLogin';

/**
 * Converts a value to a percentage of a max, clamped to the 0-100 range.
 * Callers use the result directly as a CSS width/offset percentage, so unusable
 * input (missing or non-positive max, non-finite value) yields 0 rather than
 * NaN or a negative percentage.
 * @param {number} value - current value
 * @param {number} max - target/maximum value
 * @returns {number} percentage between 0 and 100 inclusive; 0 for unusable input
 */
export const getClampedPercentage = (value, max) => {
  if (!Number.isFinite(value) || !Number.isFinite(max) || max <= 0) {
    return 0;
  }

  return Math.min(Math.max((value / max) * 100, 0), 100);
};

/**
 * Returns a random integer between min (inclusive) and max (exclusive).
 */
export function getRandomInt(min, max) {
  return Math.floor(Math.random() * (max - min)) + min;
}

export const devLog = (...args) => {
  if (!Env.isProd) {
    console.log('[DEV LOG]', ...args);
  }
};

/**
 * Shuffles the given array in place.
 */
export function shuffleArray(array) {
  const nextArray = [...array];
  let currentIndex = array.length;

  // while there are remaining elements to shuffle...
  while (currentIndex > 0) {
    // pick a random remaining element...
    const randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex -= 1;

    // and swap it with the current element.
    const temporaryValue = nextArray[currentIndex];
    nextArray[currentIndex] = nextArray[randomIndex];
    nextArray[randomIndex] = temporaryValue;
  }

  return nextArray;
}

// expands setTimeout functionality to be able to pause and get both time runing and remaining
export const setTimer = (callback = noop, delay = 0) => {
  let id;
  let startTime;
  let runningTime = 0;
  let remainingTime = delay;
  const totalTime = delay;
  let isRunning;

  const start = () => {
    isRunning = true;
    startTime = new Date();
    id = setTimeout(callback, remainingTime);
  };

  const pause = () => {
    const now = new Date();
    isRunning = false;
    clearTimeout(id);

    runningTime += now - startTime;
    remainingTime -= now - startTime;
  };

  const stop = () => {
    isRunning = false;
    clearTimeout(id);
    runningTime = 0;
    remainingTime = totalTime;
  };

  const getTimeLeft = () => {
    if (isRunning) {
      pause();
      start();
    }
    return remainingTime;
  };

  const getTimeRunning = () => {
    if (isRunning) {
      pause();
      start();
    }
    return runningTime;
  };

  const getIsRunning = () => isRunning;

  // start upon created
  start();

  return {
    start,
    pause,
    stop,
    getTimeLeft,
    getTimeRunning,
    getIsRunning,
  };
};

// shuffle and return a new array instead of mutating the existing one
export const shuffleNewArray = array => shuffleArray([...array]);

/**
 * Performs no operation.
 * @return undefined.
 */
export function noop() {
  // No operation performed.
}

/**
 * Function that  can be called in an async function to wait for an amount of time. then resume the function
 * after the time has passed.
 *
 * EX:
 * functionThatNeedsToWait = async () => {
 *  console.log('print immediatly');
 *  await wait(500);
 *  console.log('print after 500 ms');
 * }
 *
 * @param waitMs  amount of time to wait in MS
 * @returns {Promise<any>}  promise returned when wait has completed
 */
export const wait = waitMs => new Promise(resolve => { setTimeout(resolve, waitMs); });

/**
 * Return an array of indexes (aka, counting numbers) up to a given length
 * @example shuffledIndexArray(4) => [2, 1, 0, 3]
 */
export function shuffledIndexArray(length) {
  const arrayOfLengthN = Array.from(Array(length).keys());
  return shuffleArray(arrayOfLengthN);
}

export function arraysAreEqual(arr1, arr2) {
  if (arr1.length !== arr2.length) {
    return false; // why even bother?
  }

  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) {
      return false;
    }
  }

  return true;
}

export function objectsAreEqual(obj1, obj2) {
  if (obj1 === undefined || obj1 === null || obj2 === undefined || obj2 === null) {
    return obj1 === obj2;
  }
  const obj1Keys = Object.keys(obj1);
  const obj2Keys = Object.keys(obj2);
  if (obj1Keys.length !== obj2Keys.length) {
    return false; // why even bother?
  }

  for (let i = 0; i < obj1Keys.length; i++) {
    if (typeof obj1[obj1Keys[i]] === 'object') {
      if (!objectsAreEqual(obj1[obj1Keys[i]], obj2[obj1Keys[i]])) {
        return false;
      }
    } else if (obj1[obj1Keys[i]] !== obj2[obj1Keys[i]]) {
      return false;
    }
  }

  return true;
}

/**
 *
 * Selects random element of array
 */
export function selectRandomElementFromArray(array) {
  const index = Math.floor(Math.random() * array.length);

  return array[index];
}

/**
 * Randomly chooses "x" number of elements from an array
 */
export function selectRandomListFromArray(array, selectionLength) {
  const subList = [];
  const arrayCopy = [].concat(array);
  if (selectionLength > array.length) {
    if (console && console.warn) {
      console.warn(
        `Cannot select sublist of length ${selectionLength} from array of size ${array.length}!`,
        'Returning whole array',
        array,
      );
      return array;
    }
  }

  for (let i = 0; i < selectionLength; i++) {
    const index = Math.floor(Math.random() * arrayCopy.length);
    subList.push(arrayCopy[index]);
    arrayCopy.splice(index, 1);
  }

  return subList;
}

export function removeItemFromArray(item, array) {
  const index = array.indexOf(item);
  if (index > -1) {
    array.splice(index, 1);
  }

  return array;
}

export function rotateArray(array, times = 1) {
  const item = array.pop();
  array.unshift(item);

  if (times > 1) {
    return rotateArray(array, times - 1);
  }

  return array;
}

/* eslint-enable no-param-reassign */

/**
 * Checks to see if a given point is inside of a given box, as defined by a top-left corner and a width+height
 * @param {Object} point - The coordinates of point.
 * @param {number} point.x - The x position of point.
 * @param {number} point.y - The y position of point.
 * @param {Object} box - The coordinates, height and width of box.
 * @param {number} box.x - The x position of the upper-left corner of box.
 * @param {number} box.y - The y position of the upper-left corner of box.
 * @param {number} box.w - The width of box.
 * @param {number} box.h - The height of box.
 */
export function isPointInBox(point, box) {
  return (
    point.x >= box.x &&
    point.x <= box.x + box.w &&
    point.y >= box.y &&
    point.y <= box.y + box.h
  );
}

/**
 * Checks to see if innerBox is completely inside containerBox.
 * @param {Object} innerBox - The coordinates, height and width of innerBox.
 * @param {number} innerBox.x - The x position of the upper-left corner of innerBox.
 * @param {number} innerBox.y - The y position of the upper-left corner of innerBox.
 * @param {number} innerBox.width - The width of innerBox.
 * @param {number} innerBox.height - The height of innerBox.
 * @param {Object} containerBox - The coordinates, height and width of containerBox.
 * @param {number} containerBox.x - The x position of the upper-left corner of containerBox.
 * @param {number} containerBox.y - The y position of the upper-left corner of containerBox.
 * @param {number} containerBox.width - The width of containerBox.
 * @param {number} containerBox.height - The height of containerBox.
 * @returns {boolean} - true if no part of innerBox is outside of containerBox, false if a piece of innerBox is outside of containerBox.
 */
export function isBoxCompletelyInBox(innerBox, containerBox) {
  return (
    innerBox.x > containerBox.x && // left side of innerBox is inside of left side of containerBox
    innerBox.y > containerBox.y && // top side of innerBox is inside of top side of containerBox
    innerBox.x + innerBox.width < containerBox.x + containerBox.width && // right side of innerBox is inside of right side of containerBox
    innerBox.y + innerBox.height < containerBox.y + containerBox.height // bottom side of innerBox is inside of bottom side of containerBox
  );
}

/**
 * Checks to see if a any part of box1 is inside a part of box2.
 * @param {Object} box1 - The coordinates, height and width of box1.
 * @param {number} box1.x - The x position of the upper-left corner of box1.
 * @param {number} box1.y - The y position of the upper-left corner of box1.
 * @param {number} box1.width - The width of box1.
 * @param {number} box1.height - The height of box1.
 * @param {Object} box2 - The coordinates, height and width of box2.
 * @param {number} box2.x - The x position of the upper-left corner of box2.
 * @param {number} box2.y - The y position of the upper-left corner of box2.
 * @param {number} box2.width - The width of box2.
 * @param {number} box2.height - The height of box2.
 */
export function isBoxInBox(box1, box2) {
  return !(
    box1.x > box2.x + box2.width ||
    box1.x + box1.width < box2.x ||
    box1.y > box2.y + box2.height ||
    box1.y + box1.height < box2.y
  );
}

/**
 * Converts a string that contains simple tags (e.g. <b></b>, <i></i>, <color></color>)
 *  and apply styles from styleSet.
 * The list of tags can be expanded.
 * @param {string} htmlString string that might contain basic tags
 * @param {object} styleSet set of react-native styles that have keys = tag names
 * @param {object} style react-native style object that applies to outer Text element
 */
export function convertHtmlToText(htmlString, styleSet, style, props) {
  const tags = ['b', 'i', 'color'];
  const { start, tag } = tags.reduce(
    (memo, tagName) => {
      const index = htmlString.indexOf(`<${tagName}>`);
      if (index > -1 && (memo.start > index || memo.start === -1)) {
        return {
          start: index,
          tag: tagName,
        };
      }
      return memo;
    },
    { start: -1, tag: '' },
  );
  let startSearchIndex = start + 1;
  let nextOpeningTag;
  let nextClosingTag;
  do {
    nextOpeningTag = htmlString.indexOf(`<${tag}>`, startSearchIndex);
    nextClosingTag = htmlString.indexOf(`</${tag}>`, startSearchIndex);
    startSearchIndex = nextClosingTag + 1;
  } while (nextOpeningTag < nextClosingTag && nextOpeningTag > -1);
  const end = nextClosingTag;
  if (start > -1) {
    return (
      <Text style={style} {...props}>
        {convertHtmlToText(htmlString.substring(0, start), styleSet, {})}
        {convertHtmlToText(
          htmlString.substring(start + tag.length + 2, end),
          styleSet,
          styleSet[tag],
        )}
        {convertHtmlToText(
          htmlString.substring(end + tag.length + 3),
          styleSet,
          {},
        )}
      </Text>
    );
  }
  return <Text style={style} {...props}>{htmlString}</Text>;
}

export function shouldIncludeGameInFieldTrial(isGameForFieldTrial, season) {
  if (isGameForFieldTrial) {
    if (season !== SEASON.GRADE_K_1) {
      return true;
    }
    return false;
  }
  return true;
}

// Decode in case of names with spaces and make sure it's all lower case
export const formatName = (name = '') => decodeURIComponent(name).toLowerCase();

/**
 * Format and combine first and last names, and cap it at 21 characters.
 *
 * Examples:
 * formatFullName('Jordan', 'Walke') -> 'jordan walke'
 * formatFullName('Jacqueline', 'Christopherson') -> 'jacqueline christophe'
 * formatFullName('TH-622', '') -> 'th-622'
 */
export const formatFullName = (firstName, lastName) =>
  `${formatName(firstName)} ${formatName(lastName)}`.substring(0, 21).trim();

export const objectValues = obj => Object.keys(obj).map(key => obj[key]);

// function return deep Copy of object
export function deepCopy(oldObj) {
  if (oldObj && typeof oldObj === 'object') {
    const newObj = {};
    Object.keys(oldObj).forEach((key) => {
      newObj[key] = deepCopy(oldObj[key]);
    });
    return newObj;
  }
  return oldObj;
}

export function findGameInfo(gameId) {
  let result = {};
  result = GameList.filter(element => element.canonicalName === gameId, this);
  // handle case when game is not found
  if (result.length === 0) {
    result.push({
      canonicalName: 'GameUnknown',
      displayName: 'Completed',
    });
  }
  return result;
}

const getReadingGameName = gameObject => (
  gameObject.product === PRODUCT_LECTURA
    ? gameObject.name.replace(/_es$/, '')
    : gameObject.name
);

export function getGameInfoById(gameId) {
  let result = {};
  result = GameList.filter(element => element.canonicalName === gameId, this);
  // handle case when game is not found
  if (result.length === 0) {
    result.push({
      canonicalName: 'GameUnknown',
      displayName: 'Completed',
    });
  }
  return result[0];
}

/**
 * returns a subfield of an object with nested hierarchy using a string as a path through the object.
 * Example:
 * const obj = {
 *  level1: {
 *    level2: {
 *      foo: 'bar'
 *    }
 *  }
 * }
 * getObjectFieldByString(obj, 'level1.level2.foo'); // returns 'bar'
 */
export function getObjectFieldByString(object, string) {
  const pathParts = string.split('.');
  let currentSubField = { ...object };
  for (let i = 0; i < pathParts.length; i++) {
    currentSubField = currentSubField[pathParts[i]];
  }
  return currentSubField;
}

/**
 * Return whether the react node is of a given type, eg, <Text>
 * @param {PropTypes.node} reactNode - a React node to check for a given node type
 * @returns {bool}
 */
export function hasStringChild(reactNode) {
  if (reactNode.props === undefined) {
    return false;
  }
  return typeof reactNode.props.children === 'string';
}
/**
 * Recursively look for all children props and return them as a list
 * @param {PropTypes.node} children - one or many React nodes in which to search for the given node type
 * @returns {Array.<PropTypes.node>}
 */
export function getChildrenStringNodes(children) {
  const childrenArray = React.Children.toArray(children);
  let childrenMatchingType = [];
  childrenArray.forEach((childElement) => {
    if (hasStringChild(childElement)) {
      childrenMatchingType = childrenMatchingType.concat(childElement);
    } else if (childElement.props &&
      React.Children.count(childElement.props.children) > 0) {
      childrenMatchingType = childrenMatchingType.concat(
        getChildrenStringNodes(childElement.props.children),
      );
    }
  });
  return childrenMatchingType;
}

/**
 * returns new Lottie animation with layers shown/hidden.
 * layersToShow and layersToHide - array of scring names of layers in your animation JSON file
 */
export function setAnimationLayersVisibility(JSONSource, layersToShow = [], layersToHide = []) {
  const modifiedJSON = { ...JSONSource };
  if (JSONSource.layers) {
    JSONSource.layers.forEach((layer, i) => {
      if (layersToHide.includes(layer.nm)) {
        modifiedJSON.layers[i].ks.o.k = 0;
      }
      if (layersToShow.includes(layer.nm)) {
        modifiedJSON.layers[i].ks.o.k = 100;
      }
    });
  }
  if (JSONSource.assets) {
    JSONSource.assets.forEach((asset, j) => {
      if (asset.layers) {
        asset.layers.forEach((layer, i) => {
          if (layersToHide.includes(layer.nm)) {
            modifiedJSON.assets[j].layers[i].ks.o.k = 0;
          }
          if (layersToShow.includes(layer.nm)) {
            modifiedJSON.assets[j].layers[i].ks.o.k = 100;
          }
        });
      }
    });
  }
  return modifiedJSON;
}

/**
 * returns all layers in LottieView animation
 */
export function getAnimationLayersNames(JSONSource) {
  const layersNames = [];
  if (JSONSource.layers) {
    JSONSource.layers.forEach((layer) => {
      layersNames.push(layer.nm);
    });
  }
  // find nested layers
  if (JSONSource.assets) {
    JSONSource.assets.forEach((asset) => {
      if (asset.layers) {
        asset.layers.forEach((layerNested) => {
          layersNames.push(layerNested.nm);
        });
      }
    });
  }
  return layersNames;
}

const isGameSupportedOnEnvironment = (game) =>
  game.release[PRODUCT_LECTURA].includes(Env.ENVIRONMENT) ||
  game.release[PRODUCT_READING].includes(Env.ENVIRONMENT);

export function getListOfSupportedGames() {
  const result = [];
  GameList.forEach((element) => {
    let addThisOne = false;
    if (isGameSupportedOnEnvironment(element)) {
      addThisOne = true;
    }
    if (addThisOne) {
      result.push(element.canonicalName);
    }
  }, this);
  return result.sort();
}

export const isLecturaGame = game => GameList.some(listItem => (
  [listItem.canonicalName, listItem.skillScanName].includes(game)
  && (listItem.release[PRODUCT_LECTURA].includes(Env.ENVIRONMENT) || listItem.isMeasure[PRODUCT_LECTURA])
));

const isGameInGameList = gameId => GameList.some(listItem => (
  [listItem.canonicalName, listItem.skillScanName].includes(gameId)
));

export const getReportingGameId = (gameId, product = PRODUCT_READING, program = null) => {
  if (product === PRODUCT_LECTURA && isLecturaGame(gameId)) {
    return `${gameId}_es`;
  }
  if (program === PROGRAMS.ELA && isGameInGameList(gameId)) {
    return `${gameId}_ela`;
  }
  return gameId;
};

export const aquaAxios = axios.create({ timeout: 10000 });

// Use interceptors to debug failed axios calls
aquaAxios.interceptors.request.use(request => request);
aquaAxios.interceptors.response.use(
  response => response,
  error => {
    const { config, code, message, response } = error;
    const isTimeoutError = code === 'ECONNABORTED'; // timeout of 10000ms exceeded
    const errorMessage = `${isTimeoutError ? 'Timeout error:' : ''} ${code ?? message} with url ${config?.url}`;
    // prevent generic "Uncaught (in promise)" and log error with more detail
    console.error(errorMessage);

    const enhancedError = new Error(errorMessage);
    enhancedError.config = config;
    enhancedError.code = code;
    enhancedError.message = message;
    enhancedError.response = response;
    enhancedError.originalError = error;

    // reject with an Error object to give more detailed stack traces
    return Promise.reject(enhancedError);
  },
);

export const aquaAxiosWithCreds = async (url, config = {}) => {
  const token = await getToken();
  return aquaAxios(url, {
    withCredentials: true,
    headers: { Authorization: `Bearer ${token}` },
    ...config,
  });
};

/**
 * Check if app has timed out
 *
 * @param {number} lastCallTime time since last call in milliseconds
 * @returns {boolean} true if the app has timed out, false if it has not
 */

export const hasTimedOut = lastCallTime => ((Date.now() - lastCallTime) >= (MINUTES_UNTIL_TIMEOUT * 60000));

export const createArrayComparisonSelector = createSelectorCreator(
  defaultMemoize,
  arraysAreEqual,
);

// returns bucket data
// full = true - returns everything as is
// full = false - returns what is in GameData structure only
export const getBucketData = (data, full = false) => {
  if (data && data.GameData) {
    return full ? data : data.GameData;
  }
  return {};
};

// converts data we used to pass to setGameState in format compatible with saveGameState
export const dataConverter = (dataIn) => {
  const dataOut = { gameDetails: { ...dataIn } };
  return dataOut;
};

// returns true if the given substring of baseString (located at substringIndex)
// is surrounded by spaces or punctuation.
// returns false if the given substring is contained inside a larger word
export function isSubstringIsolated(baseString, substring, substringIndex) {
  const noPunctuationString = baseString.replace(/[.,/#!$%^&*;:{}=\-_`~()"']/g, ' ');

  const startIndex = Math.max(substringIndex - 1, 0);
  const endIndex = substringIndex + substring.length + 1;
  const keywordWithBookends = noPunctuationString.substring(startIndex, endIndex);

  return keywordWithBookends.trim().toLowerCase() === substring.toLowerCase();
}

export const callAsyncWithRetry = async (fn, config = {}, timesAttempted = 1) => {
  const {
    retryAttempts = 3,
    interval = 2000,
    logger = () => { },
  } = config;

  let res;
  try {
    res = await fn();
  } catch (error) {
    const isDoneRetrying = timesAttempted >= retryAttempts;
    logger(error, { timesAttempted, isDoneRetrying });
    if (error.noAsyncWithRetry || isDoneRetrying) {
      throw error;
    }
    await new Promise(resolve => { setTimeout(resolve, interval); });
    return callAsyncWithRetry(fn, config, timesAttempted + 1);
  }
  return res;
};

export const lazyWithRetry = (fn, config) => lazy(() => callAsyncWithRetry(fn, config));

export const adjustLevels = levels => levels.map(level => level - 1);

export const getGameId = (gameObject) => {
  if (gameObject.type === STEP_TYPE.EREADER) {
    return gameIds.E_READER;
  }
  if (gameObject.type === STEP_TYPE.VOCAB) {
    return gameIds.VOCAB;
  }
  return findGameInfo(getReadingGameName(gameObject))[0].canonicalName;
};

export const getGameName = (gameObject) => {
  if (gameObject.type === STEP_TYPE.EREADER) {
    return gameObject.friendlyName.replace('eReader:', '');
  }
  if (gameObject.type === STEP_TYPE.VOCAB) {
    return 'Vocab App';
  }
  const [{ canonicalName }] = findGameInfo(getReadingGameName(gameObject));
  const product = gameObject.product || PRODUCT_READING; // defaulting to reading name if no product field found
  return GameDisplayNames[product].gameDisplayNames[canonicalName];
};

export const getBookGameId = (bookId) => {
  if (bookId) {
    return `${gameIds.E_READER}.${bookId}`;
  }

  return undefined;
};

export const findChapterName = (bookId, level) => {
  const selectedBook = BookList.filter(book =>
    book.canonicalName === bookId,
  );
  if (selectedBook.length !== 1) {
    return '';
  }
  const chapterName = selectedBook[0].chapterNames[level];
  if (chapterName) {
    return chapterName;
  }
  return '';
};

export const getNewRefsArray = (length = 0) => Array.from({ length }, () => React.createRef());

// takes either multiple arguments or a single array as keys and makes an object with new refs
export const getNewRefsObjFromKeys = (...args) => {
  // if first arg is an array use that for keys, otherwise use array from each individual arg
  const keys = Array.isArray(args[0]) ? args[0] : args;
  const refsObj = {};
  for (let i = keys.length; i--;) {
    refsObj[keys[i]] = React.createRef();
  }
  return refsObj;
};

export const promiseAllSettled = (promises = []) => Promise.all(promises.map(async (promise) => {
  let value;
  try {
    value = await promise;
  } catch (e) {
    return {
      status: PROMISE_STATUSES.REJECTED,
      error: e,
      reason: e.toString(),
    };
  }
  return {
    status: PROMISE_STATUSES.FULFILLED,
    value,
  };
}));

// joins all args with '/' and then removes any extra slashes that may have created
export const makeUrlPath = (...paths) => paths.join('/').replace(/([^:]\/)\/+/g, '$1');

export const sleep = ms => new Promise(resolve => { setTimeout(resolve, ms); });

/**
 * `fetch` with a per-attempt timeout, safe to call inside `callAsyncWithRetry`.
 *
 * Each call aborts its own internal controller, so a timeout never leaves the caller's
 * `controller` aborted for the next attempt; the caller's abort is forwarded into it instead,
 * including while the body is still being read. The timeout covers the response headers only.
 *
 * @param {string} resource
 * @param {object} [options]
 * @param {number} [options.timeout=10000] ms before this attempt is aborted
 * @param {AbortController} [options.controller] caller's controller; aborting it aborts the attempt.
 *   Keep it short-lived (one request or one retry set): the forwarding listener is never unregistered.
 *   Abort it without a reason, so callers can tell the resulting AbortError from a TimeoutError
 * @returns {Promise<Response>}
 * @throws {DOMException} `TimeoutError` on timeout, `AbortError` when the caller aborts
 */
export async function aquaFetch(resource, {
  timeout = 10000,
  controller,
  ...init
} = {}) {
  const attemptController = new AbortController();
  // A local flag keeps the timeout/cancel distinction off AbortSignal.reason, which not
  // every engine propagates through fetch
  let timedOut = false;
  const id = setTimeout(() => {
    if (attemptController.signal.aborted) {
      return;
    }
    timedOut = true;
    attemptController.abort();
  }, timeout);
  const forwardAbort = () => attemptController.abort(controller?.signal.reason);
  if (controller?.signal.aborted) {
    forwardAbort();
  } else {
    controller?.signal.addEventListener('abort', forwardAbort, { once: true });
  }

  try {
    return await fetch(resource, {
      ...init,
      signal: attemptController.signal,
    });
  } catch (error) {
    if (timedOut) {
      // query string dropped: this message goes verbatim to Searchlight/Datadog and a
      // userId param would ride along with it
      const [url] = `${resource}`.split('?');
      const timeoutError = new DOMException(`Timeout error: ${timeout}ms exceeded with url ${url}`, 'TimeoutError');
      timeoutError.cause = error;
      throw timeoutError;
    }
    throw error;
  } finally {
    clearTimeout(id);
  }
}

export async function aquaFetchWithCreds(resource, init = {}) {
  const token = await getToken();
  return aquaFetch(resource, {
    ...init,
    credentials: 'same-origin',
    headers: {
      authorization: `Bearer ${token}`,
    },
  });
}

/**
 * Retrieves the current access token, refreshing it if necessary.
 *
 * Automatically checks token expiry and refreshes if needed (default: within 5 seconds).
 * Validates that a token exists after refresh. On any failure, redirects user to login.
 *
 * @returns {Promise<string>} The current valid access token
 * @throws {Error} When token refresh fails or token is missing. The error will have:
 *   - `message`: Error message from Keycloak, "missing keycloak token", or "Failed to refresh token"
 *   - `noAsyncWithRetry`: Set to true to prevent retry logic in callAsyncWithRetry
 */
export async function getToken() {
  try {
    await AuthService.updateToken();
    const token = AuthService.getAccessToken();
    if (!token) {
      throw new Error('missing keycloak token');
    }
    return token;
  } catch (tokenError) {
    const message = tokenError?.error || tokenError?.message || tokenError;
    const errorMessage = typeof message === 'string' ? message : 'Failed to refresh token';

    const error = new Error(errorMessage);
    error.noAsyncWithRetry = true;

    redirectToLogin();
    throw error;
  }
}

/**
 * returns wheather the game in quest step matches the lectura or reading game name in the action
 * @param {string} stepGame game name from quest step
 * @param {*} actionGame game name from redux action
 * @returns {bool}
 */
export const isMatchingGame = (stepGame, actionGame) => (stepGame === actionGame || stepGame === `${actionGame}_es`);

export const isGameIncompleteAndDifferentProduct = (game, currentProduct) => {
  const gameProduct = game.product || PRODUCT_READING;
  return game.levelsMastered.length < game.levels.length && gameProduct !== currentProduct;
};

/**
 * takes a skillId the levels included in the skill and the array of skillLevels for all skills with level result
 *
 * @param {*} skillId
 * @param {*} levels array of levels from content service, not adjusted
 * @param {*} skillLevels
 * @returns array of the levels that have been mastered based on skillLevel from user profile
 */
export const getLevelsMasteredFromSkillLevel = (skillId, levels, skillLevels) => {
  const skill = skillLevels.find(skillLevel => parseInt(`${skillLevel.Skill}`, 10) === parseInt(`${skillId}`, 10));
  if (!skill) return [];
  const skillProgress = skill.Level;
  const numLevelsMastered = Math.round(skillProgress * levels.length);
  return adjustLevels([...levels.slice(0, numLevelsMastered)]);
};

export const createStep = (step, source, product) => ({
  game: step.GameId,
  levels: adjustLevels(step.Levels),
  levelsMastered: [],
  type: step.GameId.includes('ereader') ? STEP_TYPE.EREADER : STEP_TYPE.GAME,
  friendlyName: step.GameId.includes('ereader') ? step.GameName : '',
  skillId: step.skillId,
  source,
  product,
});

export const filterContentSetsForUniqueEReader = (contentSets) => {
  const MAX_EREADER_GAMES_PER_SET = 1;
  const filteredSet = [];
  let currentEreaderCount = 0;
  contentSets.forEach((content) => {
    const [{ GameId }] = content.Content;
    const isEreader = GameId.includes('ereader' || 'eReader');
    if (!isEreader || currentEreaderCount < MAX_EREADER_GAMES_PER_SET) {
      currentEreaderCount += isEreader ? 1 : 0;
      filteredSet.push(content);
    }
  });
  return filteredSet;
};

/**
 * takes 2 string grades between PK and GRADE_7, if the grade is lower than the max grade,
 * that value will be returned, else the max grade is returned
 * @param {string} grade a grade in string format ex: '01', or 'PK'
 * @param {string} maxGrade a grade in string format ex: '01', or 'PK'
 * @returns {string} a grade in string format ex: '01', or 'PK'
 */
export const capGrade = (grade, maxGrade) => {
  const gradeValueMap = {
    [GRADES.PRE_KINDERGARTEN]: -1,
    [GRADES.KINDERGARTEN]: 0,
    [GRADES.GRADE_1]: 1,
    [GRADES.GRADE_2]: 2,
    [GRADES.GRADE_3]: 3,
    [GRADES.GRADE_4]: 4,
    [GRADES.GRADE_5]: 5,
    [GRADES.GRADE_6]: 6,
    [GRADES.GRADE_7]: 7,
  };
  return gradeValueMap[grade] > gradeValueMap[maxGrade] ? maxGrade : grade;
};

/**
 * Transform string in kebab-case to camelCase
 * @param {String} kebabCase string in kebab-case
 * @returns {String} new transformed string
 */
export const kebabToCamel = kebabCase =>
  kebabCase
    .split('-')
    .map(
      (word, i) =>
        (i === 0
          ? word.toLowerCase() // first word is all in lower case
          : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()), // the rest words start with capital letter
    )
    .join('');

/**
 * Transform title string (space separated words) to kebab case.
 * If optional suffix is provided, append it to the end.
 * @param {String} titleCase string to transform, may include spaces between words
 * @param {String} [suffix] optional suffix of arbitrary format
 * @returns {String} new string in kebab case
 */
/* eslint-disable no-undef */
export const titleToKebab = (titleCase, suffix = '') =>
  titleCase
    ?.concat(` ${suffix}`)
    ?.trim()
    .toLowerCase()
    .replace(/[^a-z0-9áéíóú\- ]/g, '')
    .replace(/\s+/g, '-');
/* eslint-enable no-undef */

export const formatPlacement = (placementPeriod, placementGrade) =>
  (placementPeriod && placementGrade
    ? `${placementPeriod} ${FORMATTED_GRADES[placementGrade]}`
    : undefined);

export const capitalizeFirstChar = (str) => {
  const firstLetterPos = str.search(/\p{L}|\p{N}/u);

  const firstLetter = str.charAt(firstLetterPos);
  const capitalizedStr = str.replace(firstLetter, firstLetter.toUpperCase());

  return capitalizedStr;
};

// Updates screen to loading percentage
export const showLoadingPercentage = () => {
  const spinner = document.getElementById('spinner');
  const loadingScreen = document.getElementById('loadingScreen');
  const reactWrapper = document.getElementById('reactWrapper');
  // on initial login/logout, default screen is briefly a spinner instead of loading percentage
  const isInitLoading = reactWrapper.classList.contains('hide');

  // current display: initial loading spinner (before initial APIs start after login)
  if (isInitLoading) {
    spinner.classList.add('hide');
    spinner.classList.remove('show');
  } else {
    // current display: LoadingScreen Component (spinner/glyph)
    reactWrapper.classList.add('hide');
    reactWrapper.classList.remove('show');
  }

  loadingScreen.classList.add('show');
  loadingScreen.classList.remove('hide');
};

// One-to-one conversion for React Native's Animated.modulo
export const animatedModulo = (a, b) => ((a % b) + b) % b;

export const animateTween = (element, animatedParams) => {
  if (!element || !animatedParams) {
    return;
  }

  gsap.to(element, animatedParams);
};

export const animateNestedTimeline = (elementAnimatedParams, timelineParams) => {
  const timeline = gsap.timeline({ ...timelineParams, pause: true });

  elementAnimatedParams.forEach(({ element, animatedParams }) => {
    timeline.add(animateTween(element, animatedParams));
  });

  timeline.play();
};

export const animateFlatTimeline = (elementAnimatedParams, timelineParams) => {
  const timeline = gsap.timeline({ ...timelineParams, pause: true });

  elementAnimatedParams.forEach(({ element, animatedParams }) => {
    const { orderPosition, ...params } = animatedParams;

    const tween = gsap.to(element, params);

    timeline.add(tween, orderPosition || 0);
  });

  timeline.play();
};

export const killTimelineById = (timelineId) => {
  const timeline = gsap.getById(timelineId);

  if (!timeline) {
    return;
  }

  timeline.kill();
};

/**
 * Dev Direct Game Access
 * Allows developers to directly load a specific game in dev environments.
 * Auto-initializes on module load.
 */
let devDirectGameId;
if (Env.isDevEnv) {
  const storageKey = 'devDirectGameId';

  // Load from session storage (null becomes undefined)
  const storedGameId = sessionStorage.getItem(storageKey);
  devDirectGameId = storedGameId || undefined;

  AQUA_GLOBALS.DEV = AQUA_GLOBALS.DEV || {};

  AQUA_GLOBALS.DEV.setDirectGameId = (newId) => {
    sessionStorage.setItem(storageKey, newId);
    window.location.reload();
  };
}

export const getDevDirectGameId = () => devDirectGameId;

/**
 * Determines if a game should be shown based on release environment and prototype flag
 * @param {Object} game - The game object to check
 * @param {string} product - The current product (PRODUCT_READING or PRODUCT_LECTURA)
 * @param {boolean} showGamePrototypes - Whether to show prototype games
 * @returns {boolean} - Whether the game should be displayed
 */
export const shouldShowGame = (game, product, showGamePrototypes) => {
  const isInReleaseEnvironment = game.release[product].includes(Env.ENVIRONMENT) && !game.isPrototype;
  return isInReleaseEnvironment || (game.isPrototype && showGamePrototypes);
};

export const redirectToTeacherDashboard = () => {
  sessionStorage.removeItem(SELECTED_PRODUCT_KEY);
  window.location.href = Env.TEACHER_DASHBOARD_URL;
};
